/* eslint-disable indent */
'use client';

import { useState, useRef, useEffect } from 'react';
import {
  motion,
  useScroll,
  useTransform,
  AnimatePresence,
  useInView,
} from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import Slider from 'react-slick';
import { Check, X, Share2 } from 'lucide-react';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import blogs from '../../../../data/blogs';

export default function BlogDetailPage({ blog }) {
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(null);
  const [shareResult, setShareResult] = useState(null);
  const headerRef = useRef(null);
  const contentRef = useRef(null);
  const [floatingElements, setFloatingElements] = useState([]);
  const isContentInView = useInView(contentRef, { once: false, amount: 0.1 });

  const { scrollYProgress } = useScroll({
    target: headerRef,
    offset: ['start start', 'end start'],
  });

  const { scrollYProgress: pageScrollProgress } = useScroll();

  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const textY = useTransform(scrollYProgress, [0, 0.5], [0, 100]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const blur = useTransform(scrollYProgress, [0, 1], [0, 5]);

  const imagesFromIndex2 = blog.images.slice(2);
  const relatedBlogs = blogs
    .filter(
      (relatedBlog) =>
        relatedBlog.category === blog.category &&
        relatedBlog.slug !== blog.slug,
    )
    .slice(0, 3);

  // Slider settings
  const NextArrow = ({ onClick }) => (
    <motion.button
      className='absolute top-1/2 -right-12 md:-right-16 transform -translate-y-1/2 z-20 flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-primarycolor text-white rounded-full shadow-lg'
      onClick={onClick}
      // whileHover={{ scale: 1.1 }}
      // whileTap={{ scale: 0.9 }}
    >
      <svg
        xmlns='http://www.w3.org/2000/svg'
        viewBox='0 0 24 24'
        stroke='currentColor'
        className='w-5 h-5 md:w-6 md:h-6'
      >
        <path
          d='M9 5l7 7-7 7'
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth={2}
        />
      </svg>
    </motion.button>
  );

  const PrevArrow = ({ onClick }) => (
    <motion.button
      className='absolute top-1/2 -left-12 md:-left-16 transform -translate-y-1/2 z-20 flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-primarycolor text-white rounded-full shadow-lg'
      onClick={onClick}
      // whileHover={{ scale: 1.1 }}
      // whileTap={{ scale: 0.9 }}
    >
      <svg
        xmlns='http://www.w3.org/2000/svg'
        viewBox='0 0 24 24'
        stroke='currentColor'
        className='w-5 h-5 md:w-6 md:h-6'
      >
        <path
          d='M15 5l-7 7 7 7'
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth={2}
        />
      </svg>
    </motion.button>
  );

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    autoplay: true,
    autoplaySpeed: 4000,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    beforeChange: (current, next) => setActiveImage(next),
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: true,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: true,
        },
      },
    ],
  };

  // Progress bar for reading
  const [readingProgress, setReadingProgress] = useState(0);

  useEffect(() => {
    const updateReadingProgress = () => {
      if (!document.body) return;

      const totalHeight = document.body.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setReadingProgress(progress);
    };

    window.addEventListener('scroll', updateReadingProgress);
    return () => window.removeEventListener('scroll', updateReadingProgress);
  }, []);

  // Share functionality
  // const handleShare = async (platform) => {
  //   try {
  //     const url = window.location.href;
  //     const title = blog.title;
  //     const text =
  //       typeof blog.description === 'string'
  //         ? blog.description
  //         : 'Check out this exciting real estate update from X-Space!';

  //     let shareUrl = '';

  //     switch (platform) {
  //       case 'facebook':
  //         shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
  //         break;
  //       case 'twitter':
  //         shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
  //         break;
  //       case 'linkedin':
  //         shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
  //         break;
  //       case 'copy':
  //         await navigator.clipboard.writeText(url);
  //         setShareSuccess('URL copied to clipboard!');
  //         setTimeout(() => setShareSuccess(null), 2000);
  //         setShowShareMenu(false);
  //         return;
  //     }

  //     window.open(shareUrl, '_blank', 'width=600,height=400');
  //     setShowShareMenu(false);
  //   } catch (error) {
  //     console.error('Error sharing:', error);
  //   }
  // };

  const fallbackCopyToClipboard = (text) => {
    const input = document.createElement('input');
    input.value = text;
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);
  };

  const handleShare = async (type = 'default', title, text, onSuccess, url) => {
    const shareUrl = url || window.location.href;
    const shareTitle = title || 'X-Space Real Estate';
    const shareText =
      text || 'Check out this content from X-Space Real Estate!';

    const shareData = {
      title: shareTitle,
      text: shareText,
      url: shareUrl,
    };

    if (navigator.share && type === 'default') {
      try {
        await navigator.share(shareData);
        setShareResult({ success: true, message: 'Shared successfully!' });
        onSuccess?.();
      } catch (err) {
        if (err.name !== 'AbortError') {
          setShareResult({ success: false, message: 'Error sharing' });
        }
      }
    } else {
      try {
        switch (type) {
          case 'facebook':
            window.open(
              `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
              '_blank',
            );
            break;

          case 'twitter':
            window.open(
              `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
              '_blank',
            );
            break;

          case 'linkedin':
            window.open(
              `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
              '_blank',
            );
            break;

          case 'copy':
          default:
            if (navigator.clipboard) {
              await navigator.clipboard.writeText(shareUrl);
            } else {
              fallbackCopyToClipboard(shareUrl);
            }
            setShareResult({
              success: true,
              message: 'Link copied to clipboard!',
            });
            onSuccess?.();
        }
      } catch (err) {
        setShareResult({
          success: false,
          message: 'Failed to share or copy link',
        });
      }
    }

    // Auto-clear message
    setTimeout(() => setShareResult(null), 3000);
  };

  // Floating elements animation
  // const floatingElements = Array.from({ length: 8 }).map((_, i) => ({
  //   id: i,
  //   x: Math.random() * 100,
  //   y: Math.random() * 100,
  //   size: 5 + Math.random() * 15,
  //   duration: 10 + Math.random() * 20,
  //   delay: Math.random() * 2,
  //   shape: Math.random() > 0.7 ? 'square' : 'circle',
  //   color: Math.random() > 0.5 ? 'primarycolor' : 'secondarycolor',
  // }));

  useEffect(() => {
    const generatedElements = Array.from({ length: 8 }).map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 5 + Math.random() * 15,
      duration: 10 + Math.random() * 20,
      delay: Math.random() * 2,
      shape: Math.random() > 0.7 ? 'square' : 'circle',
      color: Math.random() > 0.5 ? 'primarycolor' : 'secondarycolor',
    }));
    setFloatingElements(generatedElements);
  }, []);

  // Function to render JSX content safely
  const renderContent = (content) => {
    if (typeof content === 'string') {
      return <p>{content}</p>;
    }
    return content;
  };

  return (
    <div className='min-h-screen bg-gray-50 dark:bg-gray-900 relative overflow-hidden mt-20'>
      {/* Floating background elements */}
      {/* {floatingElements.map((element) => (
        <motion.div
          key={element.id}
          className={`fixed ${element.shape === 'square' ? 'rounded-lg' : 'rounded-full'} z-0 opacity-10 dark:opacity-20`}
          style={{
            left: `${element.x}%`,
            top: `${element.y}%`,
            width: element.size,
            height: element.size,
            background:
              element.color === 'primarycolor'
                ? 'var(--primarycolor)'
                : 'var(--secondarycolor)',
          }}
          animate={{
            y: [0, -20, 0],
            x: [0, 10, 0],
            rotate: [0, element.shape === 'square' ? 45 : 0, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            repeat: Number.POSITIVE_INFINITY,
            duration: element.duration,
            delay: element.delay,
            ease: 'easeInOut',
          }}
        />
      ))} */}

      {floatingElements.map((element) => (
        <motion.div
          key={element.id}
          className={`fixed ${
            element.shape === 'square' ? 'rounded-lg' : 'rounded-full'
          } z-0 opacity-10 dark:opacity-20`}
          style={{
            left: `${element.x}%`,
            top: `${element.y}%`,
            width: element.size,
            height: element.size,
            background:
              element.color === 'primarycolor'
                ? 'var(--primarycolor)'
                : 'var(--secondarycolor)',
          }}
          animate={{
            y: [0, -20, 0],
            x: [0, 10, 0],
            rotate: [0, element.shape === 'square' ? 45 : 0, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            repeat: Number.POSITIVE_INFINITY,
            duration: element.duration,
            delay: element.delay,
            ease: 'easeInOut',
          }}
        />
      ))}

      {/* Reading progress bar */}
      <motion.div
        className='fixed top-0 left-0 z-50 h-1 bg-gradient-to-r from-primarycolor to-secondarycolor'
        style={{ width: `${readingProgress}%` }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
      />

      {/* Hero section with parallax */}
      <div
        ref={headerRef}
        className='relative  w-full h-[85vh] overflow-hidden'
      >
        <motion.div
          className='absolute inset-0 z-0'
          style={{
            y,
            scale,
            filter: `blur(${blur}px)`,
          }}
        >
          <Image
            src={blog.images[0] || '/default-image.png'}
            alt={blog.image_alt || blog.title}
            className='w-full h-full object-cover'
            width={1920}
            height={1080}
            priority
            style={{
              filter: loading ? 'blur(20px)' : 'none',
              transition: '1s filter linear',
            }}
            onLoad={() => setLoading(false)}
            sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 40vw'
            // fill
            // sizes='(max-width: 768px) 100vw, 1200px'
            // className='object-cover'
          />

          {/* Gradient overlay */}
          <div className='absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent'></div>
        </motion.div>

        {/* Hero content */}
        <motion.div
          className='absolute inset-0 flex flex-col items-center justify-center text-center px-4 z-10'
          style={{ opacity: textOpacity, y: textY }}
        >
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className='max-w-4xl mx-auto'
          >
            <motion.div
              className='mb-6'
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{
                type: 'spring',
                stiffness: 400,
                damping: 10,
                delay: 0.2,
              }}
            >
              <motion.span
                className='inline-block px-4 py-2 bg-primarycolor text-white text-sm rounded-full'
                whileHover={{ scale: 1.05 }}
              >
                {blog.category
                  .split('_')
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ')}
              </motion.span>
            </motion.div>

            <motion.h1
              className='text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              {blog.title}
            </motion.h1>

            <motion.div
              className='flex items-center justify-center space-x-4 text-white/80'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <span>{blog.date}</span>
              <span className='w-1 h-1 bg-white/80 rounded-full'></span>
              <span>X-Space Real Estate</span>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        {/* <motion.div
          className='absolute bottom-10 left-1/2 transform -translate-x-1/2 z-10'
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Number.POSITIVE_INFINITY, duration: 1.5 }}
        >
          <div className='w-8 h-14 border-2 border-white rounded-full flex justify-center items-start p-2'>
            <motion.div
              className='w-2 h-2 bg-white rounded-full'
              animate={{ y: [0, 16, 0] }}
              transition={{ repeat: Number.POSITIVE_INFINITY, duration: 1.5 }}
            />
          </div>
        </motion.div> */}
      </div>

      {/* Main content */}
      <div className='container mx-auto px-4 py-12 relative z-10'>
        <div className='max-w-6xl mx-auto' ref={contentRef}>
          {/* Blog description */}
          <motion.div
            // initial={{ opacity: 0, y: 30 }}
            // animate={
            //   isContentInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }
            // }
            transition={{ duration: 0.3 }}
            className='bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 mb-12 relative overflow-hidden'
          >
            {/* Decorative corner accents */}
            <div className='absolute top-0 left-0 w-12 h-12 border-t-2 border-l-2 border-primarycolor rounded-tl-xl'></div>
            <div className='absolute bottom-0 right-0 w-12 h-12 border-b-2 border-r-2 border-primarycolor rounded-br-xl'></div>

            <div className='prose prose-lg max-w-none text-gray-700 dark:text-gray-300'>
              {renderContent(blog.description)}
            </div>
          </motion.div>

          {/* Blog content */}
          {blog?.contents?.map((content, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8 }}
              className='mb-12'
            >
              {content?.image && (
                <motion.div
                  className='relative w-full h-[400px] md:h-[500px] rounded-xl overflow-hidden shadow-lg mb-8'
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <Image
                    src={content.image || '/placeholder.svg'}
                    alt={content.image_alt || content.title}
                    // fill
                    width={1200}
                    height={800}
                    className='w-full h-[400px] md:h-[500px] object-cover transition-transform duration-700 hover:scale-105'
                    priority
                    style={{
                      filter: loading ? 'blur(20px)' : 'none',
                      transition: '1s filter linear',
                    }}
                    onLoad={() => setLoading(false)}
                    sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 40vw'
                  />

                  {/* Image overlay on hover */}
                  <motion.div
                    className='absolute inset-0 bg-gradient-to-t from-primarycolor/50 to-transparent opacity-0 transition-opacity duration-300'
                    whileHover={{ opacity: 1 }}
                  />
                </motion.div>
              )}

              <motion.div
                className='bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 relative overflow-hidden'
                whileHover={{
                  boxShadow:
                    '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                }}
                transition={{ duration: 0.3 }}
              >
                {/* Decorative corner accents */}
                <div className='absolute top-0 left-0 w-12 h-12 border-t-2 border-l-2 border-secondarycolor rounded-tl-xl'></div>
                <div className='absolute bottom-0 right-0 w-12 h-12 border-b-2 border-r-2 border-secondarycolor rounded-br-xl'></div>

                {/* <h2 className='sr-only'>
                  X-Space Real Estate Broker | {content.title}
                </h2> */}

                <motion.h2
                  className='text-3xl font-bold text-primarycolor dark:text-secondarycolor mb-6'
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  {content.title}
                </motion.h2>

                <motion.div
                  className='prose prose-lg max-w-none text-gray-700 dark:text-gray-300'
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  {renderContent(content.description)}
                </motion.div>
              </motion.div>
            </motion.div>
          ))}

          {/* Image gallery */}
          {imagesFromIndex2.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className='mb-16'
            >
              <motion.h3
                className='text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center'
                initial={{ opacity: 0, y: -20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                Event Gallery
              </motion.h3>

              <div className='relative px-16'>
                <Slider {...sliderSettings}>
                  {imagesFromIndex2.map((image, index) => (
                    <div key={index} className='px-2'>
                      <motion.div
                        className='relative h-[400px] md:h-[500px] rounded-xl overflow-hidden shadow-lg'
                        whileHover={{ scale: 1.02 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Image
                          src={image || '/placeholder.svg'}
                          alt={`Image ${index + 1}`}
                          // fill
                          // className='object-cover transition-transform duration-700 hover:scale-105'
                          width={1200}
                          height={800}
                          className='w-full h-[400px] md:h-[500px] object-cover'
                          priority
                          style={{
                            filter: loading ? 'blur(20px)' : 'none',
                            transition: '1s filter linear',
                          }}
                          onLoad={() => setLoading(false)}
                          sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 40vw'
                        />

                        {/* Image overlay */}
                        <motion.div
                          className='absolute inset-0 bg-gradient-to-t from-black/50 to-transparent'
                          initial={{ opacity: 0 }}
                          animate={{ opacity: activeImage === index ? 1 : 0 }}
                          transition={{ duration: 0.5 }}
                        />
                      </motion.div>
                    </div>
                  ))}
                </Slider>

                {/* Slider pagination indicators */}
                {/* <div className='flex justify-center mt-6 space-x-2'>
                  {imagesFromIndex2.map((_, index) => (
                    <motion.button
                      key={index}
                      className={`w-3 h-3 rounded-full ${
                        activeImage === index
                          ? 'bg-primarycolor'
                          : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                      whileHover={{ scale: 1.2 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => {
                        const slider = document.querySelector('.slick-slider');
                        if (slider && slider.slick) {
                          slider.slick.slickGoTo(index);
                        }
                      }}
                    />
                  ))}
                </div> */}
              </div>
            </motion.div>
          )}

          {/* Blog footer */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className='bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center relative overflow-hidden'
          >
            {/* Decorative corner accents */}
            <div className='absolute top-0 left-0 w-12 h-12 border-t-2 border-l-2 border-primarycolor rounded-tl-xl'></div>
            <div className='absolute top-0 right-0 w-12 h-12 border-t-2 border-r-2 border-primarycolor rounded-tr-xl'></div>
            <div className='absolute bottom-0 left-0 w-12 h-12 border-b-2 border-l-2 border-primarycolor rounded-bl-xl'></div>
            <div className='absolute bottom-0 right-0 w-12 h-12 border-b-2 border-r-2 border-primarycolor rounded-br-xl'></div>

            <motion.p
              className='text-xl text-gray-700 dark:text-gray-300 font-medium mb-4'
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {blog.brand_desc}
            </motion.p>

            <motion.p
              className='text-gray-500 dark:text-gray-400'
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              Published on {blog.date}
            </motion.p>

            {/* Social sharing */}
            <div className='mt-8 relative'>
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() =>
                  handleShare(
                    'default',
                    `${blog.title} | X-Space Real Estate`,
                    `${blog.meta_description || 'Read the latest insights on Dubai real estate from X-Space.'}`,
                  )
                }
                className='flex items-center justify-center mx-auto px-4 py-2 bg-primarycolor text-white rounded-full hover:bg-secondarycolor transition-colors duration-300'
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='20'
                  height='20'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  className='mr-2'
                >
                  <path d='M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8'></path>
                  <polyline points='16 6 12 2 8 6'></polyline>
                  <line x1='12' y1='2' x2='12' y2='15'></line>
                </svg>
                {/* <Share2 size={16} /> */}
                Share This Blog
              </motion.button>

              {/* Share menu */}
              <AnimatePresence>
                {showShareMenu && (
                  <motion.div
                    className='absolute left-1/2 transform -translate-x-1/2 mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-xl p-4 z-20 w-64'
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className='flex flex-col space-y-2'>
                      <motion.button
                        className='flex items-center p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors'
                        whileHover={{ x: 5 }}
                        onClick={() => handleShare('facebook')}
                      >
                        <div className='w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center mr-3'>
                          <svg
                            className='w-4 h-4'
                            fill='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path d='M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z' />
                          </svg>
                        </div>
                        Facebook
                      </motion.button>

                      <motion.button
                        className='flex items-center p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors'
                        whileHover={{ x: 5 }}
                        onClick={() => handleShare('twitter')}
                      >
                        <div className='w-8 h-8 rounded-full bg-blue-400 text-white flex items-center justify-center mr-3'>
                          <svg
                            className='w-4 h-4'
                            fill='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path d='M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z' />
                          </svg>
                        </div>
                        Twitter
                      </motion.button>

                      <motion.button
                        className='flex items-center p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors'
                        whileHover={{ x: 5 }}
                        onClick={() => handleShare('linkedin')}
                      >
                        <div className='w-8 h-8 rounded-full bg-blue-800 text-white flex items-center justify-center mr-3'>
                          <svg
                            className='w-4 h-4'
                            fill='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path d='M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z' />
                          </svg>
                        </div>
                        LinkedIn
                      </motion.button>

                      <motion.button
                        className='flex items-center p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors'
                        whileHover={{ x: 5 }}
                        onClick={() => handleShare('copy')}
                      >
                        <div className='w-8 h-8 rounded-full bg-gray-600 text-white flex items-center justify-center mr-3'>
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='16'
                            height='16'
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          >
                            <rect
                              x='9'
                              y='9'
                              width='13'
                              height='13'
                              rx='2'
                              ry='2'
                            ></rect>
                            <path d='M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1'></path>
                          </svg>
                        </div>
                        Copy Link
                      </motion.button>
                    </div>

                    {/* Close button */}
                    <motion.button
                      className='absolute top-2 right-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                      whileHover={{ rotate: 90 }}
                      onClick={() => setShowShareMenu(false)}
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='16'
                        height='16'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      >
                        <line x1='18' y1='6' x2='6' y2='18'></line>
                        <line x1='6' y1='6' x2='18' y2='18'></line>
                      </svg>
                    </motion.button>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Share success message */}
              <AnimatePresence>
                {shareResult?.success && (
                  <motion.div
                    className='flex items-center justify-between mx-auto px-4 py-2 transform -translate-x-1/2 mt-2 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-lg'
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    {shareResult?.message}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>

        {/* Related blogs */}
        {relatedBlogs.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className='mt-20'
          >
            <motion.h4
              className='text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center'
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              Related Blogs
            </motion.h4>

            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
              {relatedBlogs.map((relatedBlog, index) => (
                <motion.div
                  key={relatedBlog.slug}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  whileHover={{ y: -10 }}
                  className='group'
                >
                  <Link
                    rel='preload'
                    href={`/blog/${relatedBlog.slug}`}
                    className='block h-full'
                  >
                    <motion.div
                      className='bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 h-full flex flex-col'
                      whileHover={{ scale: 1.03 }}
                      transition={{ duration: 0.3 }}
                    >
                      {/* Image container */}
                      <div className='relative h-56 overflow-hidden'>
                        <Image
                          src={relatedBlog.images?.[0] || '/placeholder.jpg'}
                          alt={relatedBlog.image_alt || relatedBlog.title}
                          // fill
                          className='w-full h-56 object-cover transition-transform duration-700 group-hover:scale-110'
                          priority
                          style={{
                            filter: loading ? 'blur(20px)' : 'none',
                            transition: '1s filter linear',
                          }}
                          onLoad={() => setLoading(false)}
                          sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 40vw'
                          width={600}
                          height={400}
                        />

                        {/* Category badge */}
                        <motion.div
                          className='absolute top-4 left-4 bg-primarycolor text-white text-xs px-3 py-1 rounded-full z-10'
                          whileHover={{ scale: 1.1 }}
                          transition={{
                            type: 'spring',
                            stiffness: 400,
                            damping: 10,
                          }}
                        >
                          {relatedBlog.category
                            .split('_')
                            .map(
                              (word) =>
                                word.charAt(0).toUpperCase() + word.slice(1),
                            )
                            .join(' ')}
                        </motion.div>

                        {/* Gradient overlay */}
                        <motion.div
                          className='absolute inset-0 bg-gradient-to-t from-black/50 to-transparent'
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 0 }}
                          whileHover={{ opacity: 1 }}
                          transition={{ duration: 0.3 }}
                        />
                      </div>

                      {/* Content */}
                      <div className='p-6 flex flex-col flex-grow'>
                        {/* <motion.h3
                          className='text-xl font-bold text-gray-900 dark:text-white mb-3 line-clamp-2 group-hover:text-primarycolor dark:group-hover:text-secondarycolor transition-colors'
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.5, delay: 0.2 }}
                        >
                          {relatedBlog.title}
                        </motion.h3> */}

                        <motion.div
                          className='text-gray-600 dark:text-gray-300 line-clamp-3 mb-4 flex-grow'
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.5, delay: 0.3 }}
                        >
                          {typeof relatedBlog.description === 'string'
                            ? relatedBlog.description
                            : 'Read more about this exciting update...'}
                        </motion.div>

                        <div className='flex items-center justify-between mt-auto'>
                          <span className='text-sm text-gray-500 dark:text-gray-400'>
                            {relatedBlog.date}
                          </span>

                          <motion.span
                            className='text-primarycolor dark:text-secondarycolor font-medium text-sm flex items-center gap-1'
                            initial={{ opacity: 0, x: -10 }}
                            whileHover={{ x: 5 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            Read More
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width='16'
                              height='16'
                              viewBox='0 0 24 24'
                              fill='none'
                              stroke='currentColor'
                              strokeWidth='2'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            >
                              <path d='M5 12h14'></path>
                              <path d='m12 5 7 7-7 7'></path>
                            </svg>
                          </motion.span>
                        </div>
                      </div>
                    </motion.div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Back to blog button */}
        <div className='flex justify-center mt-16'>
          <Link href='/blog' rel='preload'>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className='px-8 py-3 bg-primarycolor text-white rounded-lg hover:bg-secondarycolor transition-colors duration-300 shadow-md flex items-center gap-2 relative overflow-hidden group hover:text-black'
            >
              <motion.span className='absolute inset-0 bg-gradient-to-r opacity-0 group-hover:opacity-100 transition-opacity duration-300' />
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='20'
                height='20'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
                className='relative z-10 '
              >
                <path d='M19 12H5'></path>
                <path d='M12 19l-7-7 7-7'></path>
              </svg>
              <span className='relative z-10 hover:text-black'>
                Back to All Blogs
              </span>
            </motion.button>
          </Link>
        </div>
      </div>

      {/* Scroll to top button */}
      {/* <motion.button
        className='fixed bottom-8 right-8 w-12 h-12 rounded-full bg-primarycolor dark:bg-secondarycolor text-white shadow-lg z-50 flex items-center justify-center'
        initial={{ opacity: 0, scale: 0 }}
        animate={{
          opacity: pageScrollProgress.get() > 0.1 ? 1 : 0,
          scale: pageScrollProgress.get() > 0.1 ? 1 : 0,
        }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='24'
          height='24'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
        >
          <path d='m18 15-6-6-6 6' />
        </svg>
      </motion.button> */}
    </div>
  );
}
